import { useState, useEffect, useCallback, useMemo } from 'react'
import { useDiscogsConnection, useSyncStatus } from '../context/DiscogsContext'
import { useToast } from '../context/ToastContext'
import { useViewSort } from '../context/ViewSortContext'
import { useReleases, usePageTabs, useCollection } from '../hooks'
import { removeCollectionDuplicates } from '../api/releases'
import { ReleaseFilters as ReleaseFiltersType } from '../types/release'
import { LoadingState, Pagination, ReleaseToolbar, Button, PageTabBar } from '../components/common'
import { DiscogsSyncBadge } from '../components/DiscogsWriteGate'
import { ReleaseFilters } from '../components/releases/ReleaseFilters'
import { ReleaseListView } from '../components/releases/ReleaseListView'
import styles from './PageStyles.module.css'
import releaseStyles from '../components/releases/Releases.module.css'

const DEFAULT_FILTERS: ReleaseFiltersType = { page: 1, per_page: 50, sort: 'bayesian_rating', order: 'desc' }
const SORT_DEFAULT_ASC = new Set(['title', 'released'])

export function CollectionPage() {
  const { user, isAuthenticated, isLoggingIn, login, fetchCollection } = useDiscogsConnection()
  const { syncProgress } = useSyncStatus()
  const { ids: collectionIds, dates: collectionDates } = useCollection()
  const { showToast } = useToast()
  const { viewMode, sort: globalSort, order: globalOrder } = useViewSort()

  const [showFilters, setShowFilters] = useState(false)
  const [search, setSearch] = useState(() => {
    try { const s = sessionStorage.getItem('stonks_collection_filters'); if (s) { return JSON.parse(s).q || '' } } catch {} return ''
  })

  const isSyncing = syncProgress.type === 'collection'
  const [isDeduping, setIsDeduping] = useState(false)

  const handleRemoveDuplicates = async () => {
    if (!isAuthenticated || !user) { showToast('Please login to Discogs first', 'warning'); return }
    if (!confirm('This will permanently remove duplicate entries from your Discogs collection, keeping the oldest instance of each release. Continue?')) return
    setIsDeduping(true)
    try {
      const result = await removeCollectionDuplicates()
      if (result.success) {
        showToast(result.removed > 0 ? `Removed ${result.removed} duplicate${result.removed !== 1 ? 's' : ''} from Discogs` : 'No duplicates found', result.removed > 0 ? 'success' : 'info')
        if (result.removed > 0) await fetchCollection()
      } else {
        showToast('Failed to remove duplicates', 'error')
      }
    } catch {
      showToast('Failed to remove duplicates', 'error')
    } finally {
      setIsDeduping(false)
    }
  }

  const [filters, setFilters] = useState<ReleaseFiltersType>(() => {
    try {
      const saved = sessionStorage.getItem('stonks_collection_filters')
      if (saved) return JSON.parse(saved)
    } catch { /* ignore */ }
    return { ...DEFAULT_FILTERS, sort: globalSort, order: globalOrder }
  })

  // Persist filters to sessionStorage
  useEffect(() => {
    try { sessionStorage.setItem('stonks_collection_filters', JSON.stringify(filters)) } catch {}
  }, [filters])

  // Sync global sort only when on the default tab
  const { savedSearches: tabs, activeTabId, handleTabClick, handleCreateTab, handleDeleteTab } = usePageTabs<ReleaseFiltersType>({
    scope: 'collection',
    filters,
    setFilters,
    setSearch,
    defaultFilters: { ...DEFAULT_FILTERS, sort: globalSort, order: globalOrder },
  })

  useEffect(() => {
    if (activeTabId === null) setFilters(prev => ({ ...prev, sort: globalSort, order: globalOrder }))
  }, [globalSort, globalOrder, activeTabId])

  // When sort is 'date_added', paginate IDs client-side; otherwise pass all IDs and let server sort.
  const isDateSort = filters.sort === 'date_added'
  const perPage = filters.per_page ?? 50
  const currentPage = filters.page ?? 1

  const { pageIds, dateTotal } = useMemo(() => {
    if (!isDateSort) return { pageIds: null, dateTotal: 0 }
    const sorted = [...collectionIds].sort((a, b) => {
      const da = collectionDates[a] ?? ''
      const db = collectionDates[b] ?? ''
      return filters.order === 'asc' ? da.localeCompare(db) : db.localeCompare(da)
    })
    const start = (currentPage - 1) * perPage
    return { pageIds: sorted.slice(start, start + perPage), dateTotal: sorted.length }
  }, [isDateSort, collectionIds, collectionDates, filters.order, currentPage, perPage])

  // Build final filters with collection IDs
  const queryFilters = useMemo<ReleaseFiltersType>(() => {
    if (isDateSort) {
      return { ...filters, release_ids: pageIds ?? [], sort: 'elevated_score', order: 'desc', page: 1, per_page: perPage }
    }
    return { ...filters, release_ids: collectionIds }
  }, [filters, collectionIds, isDateSort, pageIds, perPage])

  const { data, isLoading, error } = useReleases(queryFilters, {
    enabled: isAuthenticated && collectionIds.length > 0,
  })

  // Re-sort returned releases by date_added client-side (server doesn't know the order)
  const displayReleases = useMemo(() => {
    if (!isDateSort || !data?.releases) return data?.releases
    const dir = filters.order === 'asc' ? 1 : -1
    return [...data.releases].sort((a, b) => {
      const da = collectionDates[a.id] ?? ''
      const db = collectionDates[b.id] ?? ''
      return da < db ? -dir : da > db ? dir : 0
    })
  }, [data?.releases, isDateSort, collectionDates, filters.order])

  const paginationInfo = isDateSort ? {
    page: currentPage,
    pages: Math.ceil(dateTotal / perPage),
    has_next: currentPage * perPage < dateTotal,
    has_prev: currentPage > 1,
  } : data?.pagination ? {
    page: data.pagination.page,
    pages: data.pagination.total_pages,
    has_next: data.pagination.page < data.pagination.total_pages,
    has_prev: data.pagination.page > 1,
  } : undefined

  const handleSearch = useCallback((value: string) => {
    setSearch(value)
    setFilters(prev => ({ ...prev, q: value || undefined, page: 1 }))
  }, [])

  const handlePageChange = (newPage: number) => {
    setFilters(prev => ({ ...prev, page: newPage }))
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const handleSync = async () => {
    if (!isAuthenticated || !user) { showToast('Please login to Discogs first', 'warning'); return }
    if (syncProgress.type) { showToast('Already syncing, please wait', 'warning'); return }
    try {
      await fetchCollection()
      showToast('Collection synced successfully', 'success')
    } catch {
      showToast('Failed to sync collection', 'error')
    }
  }

  if (!isAuthenticated) {
    return (
      <div className={styles.page}>
        <div className={styles.pageHeader}><h1>Collection</h1></div>
        <div className={styles.empty} data-tour="discogs-connect-section">
          <i className="bi bi-vinyl"></i>
          <div className={styles.emptyTitle}>Connect your Discogs account</div>
          <p>Sync your collection and pull in live marketplace data from Discogs.</p>
          <Button
            variant="primary"
            icon={`bi ${isLoggingIn ? 'bi-arrow-repeat' : 'bi-vinyl'}`}
            onClick={login}
            disabled={isLoggingIn}
            data-tour="discogs-connect-cta"
          >
            {isLoggingIn ? 'Connecting…' : 'Connect Discogs'}
          </Button>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className={styles.page}>
        <div className={styles.pageHeader}><h1>Collection</h1></div>
        <div className={styles.error}>
          <i className="bi bi-exclamation-triangle"></i>
          <p>Failed to load collection releases</p>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <div className={styles.pageHeader}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
          <h1>Collection</h1>
          <DiscogsSyncBadge list="collection" />
          <Button variant="accentSoft" icon={`bi ${isSyncing ? 'bi-arrow-repeat' : 'bi-arrow-clockwise'}`} onClick={handleSync} disabled={isSyncing || !isAuthenticated} title="Sync collection from Discogs" data-tour="discogs-sync-btn">
            {isSyncing
              ? syncProgress.total > 0
                ? `Syncing page ${syncProgress.current}/${syncProgress.total} (${syncProgress.totalItems.toLocaleString()} items)...`
                : 'Starting sync...'
              : 'Sync'}
          </Button>
          <Button variant="accentSoft" icon={`bi ${isDeduping ? 'bi-arrow-repeat' : 'bi-subtract'}`} onClick={handleRemoveDuplicates} disabled={isDeduping || isSyncing || !isAuthenticated} title="Remove duplicate entries from Discogs collection">
            {isDeduping ? 'Removing...' : 'Remove Duplicates'}
          </Button>
        </div>
      </div>

      <PageTabBar
        tabs={tabs}
        activeTabId={activeTabId}
        defaultLabel="All Collection"
        onTabClick={handleTabClick}
        onCreateTab={handleCreateTab}
        onDeleteTab={handleDeleteTab}
      />

      <ReleaseToolbar
        search={search}
        onSearchChange={setSearch}
        onSearchSubmit={handleSearch}
        searchPlaceholder="Search collection..."
        showFilters={showFilters}
        onToggleFilters={() => setShowFilters(!showFilters)}
        filters={filters}
        onFiltersChange={update => setFilters(prev => ({ ...prev, ...update }))}
        viewSortProps={{
          extraOptions: [{ value: 'date_added', label: 'Date Added' }],
          ...(activeTabId !== null ? {
            sort: filters.sort,
            order: filters.order,
            onSortChange: (sort: string) =>
              setFilters(prev => ({ ...prev, sort, order: SORT_DEFAULT_ASC.has(sort) ? 'asc' as const : 'desc' as const, page: 1 })),
            onToggleOrder: () =>
              setFilters(prev => ({ ...prev, order: prev.order === 'desc' ? 'asc' as const : 'desc' as const })),
          } : {}),
        }}
      />

      {showFilters && <ReleaseFilters filters={filters} onFiltersChange={setFilters} onClose={() => setShowFilters(false)} contextReleaseIds={collectionIds} />}

      {collectionIds.length === 0 && !isSyncing ? (
        <div className={styles.empty}>
          <i className="bi bi-collection"></i>
          <p>Your collection hasn't been synced yet</p>
          <Button variant="primary" icon="bi bi-arrow-clockwise" onClick={handleSync}>
            Sync Collection
          </Button>
        </div>
      ) : isLoading ? (
        <LoadingState message="Loading collection..." />
      ) : (
        <div className={releaseStyles.releasesLayout}>
          <div className={releaseStyles.tableContainer}>
            <ReleaseListView
              releases={displayReleases ?? []}
              viewMode={viewMode}
              emptyIcon="bi bi-collection"
              emptyMessage="No matching releases"
              emptyHint="Try adjusting your filters"
            />

            {paginationInfo && (
              <Pagination
                page={paginationInfo.page}
                totalPages={paginationInfo.pages}
                hasNext={paginationInfo.has_next}
                hasPrev={paginationInfo.has_prev}
                onPageChange={handlePageChange}
                perPage={filters.per_page}
                onPerPageChange={(per_page) => setFilters(prev => ({ ...prev, per_page, page: 1 }))}
              />
            )}
          </div>
        </div>
      )}
    </div>
  )
}
